import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "antd";
import { AudioOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Toaster } from "../../utility/Toaster";
const { TextArea } = Input;

const LANGUAGE_MAP = {
    en: "en-US",
    ar: "ar-SA",
    ur: "ur-PK",
    fr: "fr-FR",
    so: "so-SO",
    ru: "ru-RU",
    hi: "hi-IN",
    es: "es-ES",
    vi: "vi-VN",
    ko: "ko-KR",
    th: "th-TH",
    my: "my-MM",
    hmn: "hmn",
};

const getRecognitionLanguage = () => {
    const stored = (localStorage.getItem("language") || "").trim();
    const normalized = stored.toLowerCase();
    return (
        LANGUAGE_MAP[stored] ||
        LANGUAGE_MAP[normalized] ||
        (stored.includes("-") ? stored : "en-US")
    );
};

const buildValue = (base, finalText, interimText) => {
    const combined = `${finalText}${interimText}`;
    if (!combined) return base;
    if (!base) return combined;
    const needsSpace = !/\s$/.test(base);
    return needsSpace ? `${base} ${combined}` : `${base}${combined}`;
};

const getAppendMeta = (base, finalText) => {
    const trimmedFinal = (finalText || "").trim();
    if (!trimmedFinal) return null;
    const needsSpace = base && !/\s$/.test(base);
    const appendedText = `${needsSpace ? " " : ""}${finalText}`;
    return {
        text: trimmedFinal,
        baseValue: base,
        appendedText,
    };
};

const VoiceInput = ({
    as = "input",
    onChange,
    value,
    defaultValue,
    disabled,
    suffix,
    ...rest
}) => {
    const [isSupported, setIsSupported] = useState(true);
    const [isListening, setIsListening] = useState(false);
    const [innerValue, setInnerValue] = useState(defaultValue ?? "");
    const [lastSegment, setLastSegment] = useState(null);

    const recognitionRef = useRef(null);
    const restartTimeoutRef = useRef(null);
    const lastStartRef = useRef(0);
    const retryCountRef = useRef(0);
    const silenceTimeoutRef = useRef(null);
    const hasSpokenRef = useRef(false);
    const lastInterimRef = useRef("");
    const baseValueRef = useRef("");
    const finalTranscriptRef = useRef("");
    const valueRef = useRef(value ?? innerValue ?? "");
    const onChangeRef = useRef(onChange);
    const isListeningRef = useRef(false);
    const shouldListenRef = useRef(false);
    const warnedRef = useRef(false);

    const inputValue = value !== undefined ? value : innerValue;

    useEffect(() => {
        valueRef.current = value !== undefined ? value : innerValue;
    }, [value, innerValue]);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setIsSupported(false);
            if (!warnedRef.current) {
                console.warn("Speech Recognition is not supported in this browser.");
                Toaster("info", "ATTENTION", "Speech Recognition is not supported in this browser.");
                warnedRef.current = true;
            }
            return undefined;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognitionRef.current = recognition;

        const scheduleRestart = (delayMs) => {
            if (!shouldListenRef.current || !recognitionRef.current) return;
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
            }
            restartTimeoutRef.current = setTimeout(() => {
                if (!recognitionRef.current || !shouldListenRef.current) return;
                const now = Date.now();
                if (now - lastStartRef.current < 300) {
                    scheduleRestart(400);
                    return;
                }
                try {
                    recognitionRef.current.start();
                    lastStartRef.current = now;
                    retryCountRef.current = 0;
                } catch (error) {
                    retryCountRef.current += 1;
                    scheduleRestart(Math.min(1200, 300 + retryCountRef.current * 200));
                }
            }, delayMs);
        };

        const scheduleSilenceStop = () => {
            if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
            }
            silenceTimeoutRef.current = setTimeout(() => {
                if (!shouldListenRef.current || !hasSpokenRef.current) return;
                stopListening();
            }, 1200);
        };

        recognition.onresult = (event) => {
            let finalText = finalTranscriptRef.current;
            let interimText = "";

            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                const result = event.results[i];
                const transcript = result[0]?.transcript || "";
                if (result.isFinal) {
                    finalText += transcript;
                } else {
                    interimText += transcript;
                }
            }

            finalTranscriptRef.current = finalText;
            lastInterimRef.current = interimText;
            const updatedValue = buildValue(baseValueRef.current, finalText, interimText);

            if (finalText || interimText) {
                hasSpokenRef.current = true;
                scheduleSilenceStop();
            }

            if (value === undefined) {
                setInnerValue(updatedValue);
            }

            if (onChangeRef.current) {
                onChangeRef.current({ target: { value: updatedValue } });
            }
        };

        recognition.onerror = (event) => {
            console.warn("Speech recognition error:", event.error);
            if (
                event.error === "not-allowed" ||
                event.error === "service-not-allowed" ||
                event.error === "audio-capture"
            ) {
                shouldListenRef.current = false;
                isListeningRef.current = false;
                setIsListening(false);
                return;
            }
            if (event.error === "no-speech" || event.error === "aborted") {
                scheduleRestart(350);
            }
        };

        recognition.onstart = () => {
            isListeningRef.current = true;
            setIsListening(true);
        };

        recognition.onend = () => {
            isListeningRef.current = false;
            if (shouldListenRef.current && hasSpokenRef.current && finalTranscriptRef.current.trim()) {
                shouldListenRef.current = false;
                setIsListening(false);
                finalizeLastSegment();
                return;
            }
            if (shouldListenRef.current) {
                setIsListening(true);
                if (recognitionRef.current) {
                    scheduleRestart(400);
                }
            } else {
                setIsListening(false);
            }
        };

        return () => {
            isListeningRef.current = false;
            shouldListenRef.current = false;
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
            }
            if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
            }
            recognition.stop();
            recognitionRef.current = null;
        };
    }, []);

    const handleChange = useCallback(
        (event) => {
            if (value === undefined) {
                setInnerValue(event.target.value);
            }
            if (onChange) {
                onChange(event);
            }
        },
        [onChange, value]
    );

    const finalizeLastSegment = useCallback(() => {
        const finalText = finalTranscriptRef.current.trim()
            ? finalTranscriptRef.current
            : lastInterimRef.current;
        const meta = getAppendMeta(baseValueRef.current, finalText);
        if (meta) {
            setLastSegment(meta);
        }
    }, []);

    const stopListening = useCallback(() => {
        if (recognitionRef.current) {
            shouldListenRef.current = false;
            retryCountRef.current = 0;
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
            }
            if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
            }
            setIsListening(false);
            recognitionRef.current.stop();
            finalizeLastSegment();
        }
    }, [finalizeLastSegment]);

    const startListening = useCallback(() => {
        if (!recognitionRef.current) return;
        shouldListenRef.current = true;
        retryCountRef.current = 0;
        hasSpokenRef.current = false;
        lastInterimRef.current = "";
        setIsListening(true);
        setLastSegment(null);
        baseValueRef.current = valueRef.current || "";
        finalTranscriptRef.current = "";
        recognitionRef.current.lang = getRecognitionLanguage();
        try {
            recognitionRef.current.start();
            lastStartRef.current = Date.now();
        } catch (error) {
            // Ignore start errors when already running
        }
    }, []);

    const handleAcceptSegment = useCallback(() => {
        setLastSegment(null);
    }, []);

    const handleClearSegment = useCallback(() => {
        if (!lastSegment) return;
        const currentValue = valueRef.current || "";
        let nextValue = currentValue;
        if (currentValue.endsWith(lastSegment.appendedText)) {
            nextValue = currentValue.slice(0, currentValue.length - lastSegment.appendedText.length);
        } else if (currentValue.startsWith(lastSegment.baseValue)) {
            nextValue = lastSegment.baseValue;
        }
        if (value === undefined) {
            setInnerValue(nextValue);
        }
        if (onChangeRef.current) {
            onChangeRef.current({ target: { value: nextValue } });
        }
        valueRef.current = nextValue;
        setLastSegment(null);
    }, [lastSegment, value]);

    const toggleListening = useCallback(() => {
        if (disabled || !isSupported) return;
        if (isListeningRef.current) {
            stopListening();
        } else {
            startListening();
        }
    }, [disabled, isSupported, startListening, stopListening]);

    const micButton = useMemo(() => {
        const color = isListening ? "#fa541c" : undefined;
        return (
            <button
                type="button"
                className={`voice-input-button${isListening ? " is-listening" : ""}`}
                onClick={toggleListening}
                disabled={disabled || !isSupported}
                title={isSupported ? (isListening ? "Stop voice input" : "Start voice input") : "This functionality is not supported in this browser"}
                aria-label="Toggle voice input"
                aria-pressed={isListening}
                style={{
                    cursor: disabled || !isSupported ? "not-allowed" : "pointer",
                }}
            >
                <AudioOutlined style={{ color }} />
            </button>
        );
    }, [disabled, isListening, isSupported, toggleListening]);

    const actionButtons = useMemo(() => {
        if (isListening || !lastSegment) return null;
        return (
            <div className="voice-input-actions">
                <button
                    type="button"
                    className="voice-input-action-button accept"
                    onClick={handleAcceptSegment}
                    aria-label="Keep last spoken text"
                >
                    <CheckOutlined />
                </button>
                <button
                    type="button"
                    className="voice-input-action-button clear"
                    onClick={handleClearSegment}
                    aria-label="Clear last spoken text"
                >
                    <CloseOutlined />
                </button>
            </div>
        );
    }, [handleAcceptSegment, handleClearSegment, isListening, lastSegment]);

    const lastSegmentNote = useMemo(() => {
        if (!lastSegment || isListening) return null;
        return (
            <div className="voice-input-highlight">
                <span className="voice-input-highlight-label">Last spoken:</span>
                <span className="voice-input-highlight-text">{lastSegment.text}</span>
            </div>
        );
    }, [isListening, lastSegment]);

    if (as === "textarea") {
        return (
            <>
                <div className="voice-input-wrapper">
                    <TextArea
                        {...rest}
                        value={inputValue}
                        disabled={disabled}
                        onChange={handleChange}
                    />
                    <div className="voice-input-action">
                        {micButton}
                        {actionButtons}
                    </div>
                </div>
                {lastSegmentNote}
            </>
        );
    }

    return (
        <>
            <div className="voice-input-wrapper">
                <Input
                    {...rest}
                    value={inputValue}
                    disabled={disabled}
                    onChange={handleChange}
                    suffix={suffix}
                />
                <div className="voice-input-action">
                    {micButton}
                    {actionButtons}
                </div>
            </div>
            {lastSegmentNote}
        </>
    );
};

export default VoiceInput;
